import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Bell, Calendar, CreditCard, Globe, Heart, Instagram, Languages, Link2, LogOut, MapPin, Megaphone,
  Receipt, ScanLine, ShieldCheck, Smartphone, Sparkles, Ticket,
} from "lucide-react";
import { toast } from "sonner";
import QRTicketDialog from "@/components/QRTicketDialog";
import { applyPosterFallback, getEventId, getEventPoster } from "@/lib/eventDisplay";

const roleLabel = (user) => {
  if (user?.role === "admin" || user?.is_admin) return "Admin";
  if (user?.role === "organizer") return "Organiser";
  return "Normal user";
};

const normalisePrefs = (user) => {
  const prefs = user?.language_preferences;
  if (Array.isArray(prefs) && prefs.length) return prefs;
  return [user?.ad_language_pref || "All"];
};

const normaliseCityPrefs = (user) => {
  const prefs = user?.city_preferences;
  if (Array.isArray(prefs) && prefs.length) return prefs;
  return ["All UK"];
};

const CREATOR_CATEGORIES = [
  "Personal", "Business", "Influencer", "Food Blogger", "Event Promoter",
  "Artist", "DJ", "Dancer", "Venue", "Community Group", "Sponsor",
];

const CREATOR_INTERESTS = [
  "Garba/Navratri", "Music", "Comedy", "Theatre", "Food", "Family/Kids",
  "Business Networking", "Religious/Community", "Nightlife", "Wellness",
];

const normaliseList = (value) => Array.isArray(value) ? value : [];

export default function Profile() {
  const { user, updateProfile, logout, loading, setUser } = useAuth();
  const { languages, cities } = useApp();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [mobile, setMobile] = useState("");
  const [whatsappOptIn, setWhatsappOptIn] = useState(false);
  const [instagramHandle, setInstagramHandle] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");
  const [tiktokUrl, setTiktokUrl] = useState("");
  const [facebookUrl, setFacebookUrl] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [bio, setBio] = useState("");
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [publicProfile, setPublicProfile] = useState(false);
  const [creatorOptIn, setCreatorOptIn] = useState(false);
  const [creatorCategories, setCreatorCategories] = useState([]);
  const [creatorInterests, setCreatorInterests] = useState([]);
  const [creatorLanguages, setCreatorLanguages] = useState([]);
  const [creatorLocations, setCreatorLocations] = useState([]);
  const [languagePrefs, setLanguagePrefs] = useState(["All"]);
  const [cityPrefs, setCityPrefs] = useState(["All UK"]);
  const [myEvents, setMyEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [saving, setSaving] = useState(false);
  const [emailCode, setEmailCode] = useState("");
  const [emailVerificationMessage, setEmailVerificationMessage] = useState("");
  const [sendingEmailCode, setSendingEmailCode] = useState(false);
  const [checkingEmailCode, setCheckingEmailCode] = useState(false);

  useEffect(() => {
    if (!user) return;
    setName(user.name || "");
    setDisplayName(user.display_name || "");
    setMobile(user.mobile || "");
    setWhatsappOptIn(Boolean(user.whatsapp_opt_in));
    setInstagramHandle(user.instagram_handle || "");
    setInstagramUrl(user.instagram_url || "");
    setTiktokUrl(user.tiktok_url || "");
    setFacebookUrl(user.facebook_url || "");
    setYoutubeUrl(user.youtube_url || "");
    setWebsiteUrl(user.website_url || "");
    setBio(user.bio || "");
    setProfileImageUrl(user.profile_image_url || "");
    setPublicProfile(Boolean(user.public_profile));
    setCreatorOptIn(Boolean(user.creator_opt_in));
    setCreatorCategories(normaliseList(user.creator_categories));
    setCreatorInterests(normaliseList(user.creator_interests));
    setCreatorLanguages(normaliseList(user.creator_languages));
    setCreatorLocations(normaliseList(user.creator_locations));
    setLanguagePrefs(normalisePrefs(user));
    setCityPrefs(normaliseCityPrefs(user));
    Promise.allSettled([
      api.get("/events/my"),
      api.get("/bookings"),
      api.get("/notifications"),
    ]).then(([eventsRes, bookingsRes, notificationsRes]) => {
      if (eventsRes.status === "fulfilled") setMyEvents(eventsRes.value.data || []);
      if (bookingsRes.status === "fulfilled") setBookings(bookingsRes.value.data || []);
      if (notificationsRes.status === "fulfilled") setNotifications(notificationsRes.value.data || []);
    });
  }, [user]);

  const languageOptions = useMemo(() => {
    const values = ["All", ...(languages || []).map((l) => l.name).filter(Boolean)];
    return [...new Set(values)];
  }, [languages]);

  const cityOptions = useMemo(() => {
    const values = ["All UK", ...(cities || []).filter(Boolean)];
    return [...new Set(values)];
  }, [cities]);

  const upcomingBookings = bookings.filter((b) => b.status !== "refunded" && b.status !== "cancelled").slice(0, 3);
  const unreadCount = notifications.filter((n) => !n.read).length;
  const role = roleLabel(user);

  if (loading) return <div className="min-h-screen bg-background"><Navbar /></div>;
  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="mx-auto max-w-md p-10 text-center">
          <h2 className="font-display text-2xl font-bold">Sign in to view your profile</h2>
          <Button onClick={() => navigate("/login")} className="mt-4 rounded-full">Sign in</Button>
        </div>
      </div>
    );
  }

  const toggleLanguage = (lang, checked) => {
    if (lang === "All") {
      setLanguagePrefs(checked ? ["All"] : []);
      return;
    }
    setLanguagePrefs((current) => {
      const withoutAll = current.filter((x) => x !== "All");
      const next = checked ? [...withoutAll, lang] : withoutAll.filter((x) => x !== lang);
      return next.length ? [...new Set(next)] : ["All"];
    });
  };

  const toggleCity = (city, checked) => {
    if (city === "All UK") {
      setCityPrefs(checked ? ["All UK"] : []);
      return;
    }
    setCityPrefs((current) => {
      const withoutAll = current.filter((x) => x !== "All UK");
      const next = checked ? [...withoutAll, city] : withoutAll.filter((x) => x !== city);
      return next.length ? [...new Set(next)] : ["All UK"];
    });
  };

  const toggleListValue = (setter, value, checked) => {
    setter((current) => {
      const next = checked ? [...current, value] : current.filter((x) => x !== value);
      return [...new Set(next)];
    });
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await updateProfile({
        name,
        display_name: displayName,
        mobile,
        whatsapp_opt_in: whatsappOptIn,
        instagram_handle: instagramHandle,
        instagram_url: instagramUrl,
        tiktok_url: tiktokUrl,
        facebook_url: facebookUrl,
        youtube_url: youtubeUrl,
        website_url: websiteUrl,
        bio,
        profile_image_url: profileImageUrl,
        public_profile: publicProfile,
        creator_opt_in: creatorOptIn,
        creator_categories: creatorOptIn ? creatorCategories : [],
        creator_interests: creatorOptIn ? creatorInterests : [],
        creator_languages: creatorOptIn ? creatorLanguages : [],
        creator_locations: creatorOptIn ? creatorLocations : [],
        language_preferences: languagePrefs,
        city_preferences: cityPrefs,
      });
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Profile update failed");
    } finally {
      setSaving(false);
    }
  };

  const sendEmailCode = async () => {
    setSendingEmailCode(true);
    setEmailVerificationMessage("");
    try {
      const { data } = await api.post("/auth/email-verification/start");
      const message = data.debug_code
        ? `Demo code: ${data.debug_code}`
        : data.message || "Verification code sent.";
      setEmailVerificationMessage(message);
      if (data.delivery === "sent") toast.success("Verification code sent");
      else toast.info(message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send verification code");
    } finally {
      setSendingEmailCode(false);
    }
  };

  const confirmEmailCode = async () => {
    setCheckingEmailCode(true);
    try {
      const { data } = await api.post("/auth/email-verification/confirm", { code: emailCode });
      setUser(data);
      setEmailCode("");
      setEmailVerificationMessage("");
      toast.success("Email verified");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not verify email code");
    } finally {
      setCheckingEmailCode(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="page-shell py-8 space-y-6">
        {user.role === "organizer" && <OrganizerFinancePanel />}

        <section className="rounded-2xl border border-border p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-5">
            <Avatar className="h-20 w-20">
              <AvatarImage src={user.picture} />
              <AvatarFallback>{(user.name || "U").slice(0, 1)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-display text-2xl md:text-3xl font-black">{user.name}</h1>
                <Badge variant={role === "Admin" ? "default" : "outline"}>{role}</Badge>
                {unreadCount > 0 && <Badge variant="secondary">{unreadCount} unread</Badge>}
              </div>
              <p className="text-sm text-muted-foreground mt-1">{user.email}</p>
              <div className="mt-4 grid grid-cols-3 gap-2 max-w-xl">
                <Stat label="Bookings" value={bookings.length} />
                <Stat label="My events" value={myEvents.length} />
                <Stat label="Notifications" value={notifications.length} />
              </div>
            </div>
            <Button variant="outline" onClick={async () => { await logout(); navigate("/"); }} data-testid="logout-btn" className="rounded-full">
              <LogOut className="h-4 w-4 mr-2" /> Log out
            </Button>
          </div>
        </section>

        <section className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-2xl border border-border p-5 md:p-6 space-y-6">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-black text-primary">Account details</p>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold">Name</label>
                  <Input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-bold">Display name</label>
                  <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Name shown publicly" />
                </div>
                <div>
                  <label className="text-xs font-bold">Mobile</label>
                  <Input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="+44..." />
                </div>
                <ReadOnlyField label="Role" value={role} />
                <ReadOnlyField label="Email" value={user.email} />
                <ReadOnlyField label="User ID" value={user.user_id} mono />
              </div>
              <div className="mt-4 rounded-xl border border-border bg-muted/30 p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-black">Organiser readiness</p>
                  <Badge variant={user.email_verified ? "default" : "secondary"}>
                    {user.email_verified ? "Email verified" : "Email verification needed"}
                  </Badge>
                  <Badge variant={mobile ? "default" : "secondary"}>
                    {mobile ? "Mobile saved" : "Mobile required"}
                  </Badge>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Organisers must verify email by code and save a mobile contact number before creating or resubmitting events. Mobile is contact-only; no SMS OTP is required.
                </p>
                {!user.email_verified && (
                  <div className="mt-3 grid gap-2 sm:grid-cols-[auto_1fr_auto]">
                    <Button type="button" variant="outline" onClick={sendEmailCode} disabled={sendingEmailCode} className="rounded-full">
                      {sendingEmailCode ? "Sending..." : "Send email code"}
                    </Button>
                    <Input
                      value={emailCode}
                      onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="6 digit code"
                      inputMode="numeric"
                    />
                    <Button type="button" onClick={confirmEmailCode} disabled={checkingEmailCode || emailCode.length !== 6} className="rounded-full">
                      {checkingEmailCode ? "Checking..." : "Verify"}
                    </Button>
                  </div>
                )}
                {emailVerificationMessage && <p className="mt-2 text-xs text-muted-foreground">{emailVerificationMessage}</p>}
              </div>
            </div>

            <div className="border-t border-border pt-5">
              <div className="flex items-center gap-2">
                <Instagram className="h-4 w-4 text-primary" />
                <p className="text-xs uppercase tracking-[0.25em] font-black text-primary">Social profile</p>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Optional details for organiser collaboration, creator discovery, and future social sharing.
              </p>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold">Instagram handle</label>
                  <Input value={instagramHandle} onChange={(e) => setInstagramHandle(e.target.value)} placeholder="@namastenetwork" />
                </div>
                <div>
                  <label className="text-xs font-bold">Instagram URL</label>
                  <Input value={instagramUrl} onChange={(e) => setInstagramUrl(e.target.value)} placeholder="https://instagram.com/..." />
                </div>
                <div>
                  <label className="text-xs font-bold">TikTok URL</label>
                  <Input value={tiktokUrl} onChange={(e) => setTiktokUrl(e.target.value)} placeholder="https://tiktok.com/@..." />
                </div>
                <div>
                  <label className="text-xs font-bold">Facebook page/profile</label>
                  <Input value={facebookUrl} onChange={(e) => setFacebookUrl(e.target.value)} placeholder="https://facebook.com/..." />
                </div>
                <div>
                  <label className="text-xs font-bold">YouTube channel</label>
                  <Input value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} placeholder="https://youtube.com/..." />
                </div>
                <div>
                  <label className="text-xs font-bold">Website</label>
                  <Input value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} placeholder="https://..." />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-bold">Bio</label>
                  <Textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3} placeholder="Short intro for your profile or creator page" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-bold">Profile image URL</label>
                  <Input value={profileImageUrl} onChange={(e) => setProfileImageUrl(e.target.value)} placeholder="https://..." />
                </div>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 gap-2">
                <ToggleRow
                  checked={whatsappOptIn}
                  onCheckedChange={setWhatsappOptIn}
                  icon={Smartphone}
                  title="WhatsApp updates"
                  body="Allow us to use your mobile number for future WhatsApp notifications."
                />
                <ToggleRow
                  checked={publicProfile}
                  onCheckedChange={setPublicProfile}
                  icon={Globe}
                  title="Public profile"
                  body="Make selected profile details eligible for future public creator pages."
                />
              </div>
            </div>

            <div className="border-t border-border pt-5">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <p className="text-xs uppercase tracking-[0.25em] font-black text-primary">Creator and influencer profile</p>
              </div>
              <ToggleRow
                checked={creatorOptIn}
                onCheckedChange={setCreatorOptIn}
                icon={Link2}
                title="Highlight me as a creator, influencer, business or community profile"
                body="This keeps your normal account but lets admins and organisers classify you for campaigns and collaborations."
                className="mt-4"
              />
              {creatorOptIn && (
                <div className="mt-4 space-y-5">
                  <ChipGroup
                    title="Creator category"
                    options={CREATOR_CATEGORIES}
                    selected={creatorCategories}
                    onToggle={(value, checked) => toggleListValue(setCreatorCategories, value, checked)}
                  />
                  <ChipGroup
                    title="Content and event interests"
                    options={CREATOR_INTERESTS}
                    selected={creatorInterests}
                    onToggle={(value, checked) => toggleListValue(setCreatorInterests, value, checked)}
                  />
                  <ChipGroup
                    title="Creator languages"
                    options={languageOptions.filter((x) => x !== "All")}
                    selected={creatorLanguages}
                    onToggle={(value, checked) => toggleListValue(setCreatorLanguages, value, checked)}
                  />
                  <ChipGroup
                    title="Creator locations"
                    options={cityOptions.filter((x) => x !== "All UK")}
                    selected={creatorLocations}
                    onToggle={(value, checked) => toggleListValue(setCreatorLocations, value, checked)}
                  />
                </div>
              )}
            </div>

            <div className="border-t border-border pt-5">
              <div className="flex items-center gap-2">
                <Languages className="h-4 w-4 text-primary" />
                <p className="text-xs uppercase tracking-[0.25em] font-black text-primary">Language preferences</p>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Used for recommendations, featured ads, reminders, and future notification targeting.
              </p>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {languageOptions.map((lang) => (
                  <label key={lang} className="flex items-center gap-2 rounded-xl border border-border p-3 text-sm">
                    <Checkbox
                      checked={languagePrefs.includes(lang)}
                      onCheckedChange={(checked) => toggleLanguage(lang, Boolean(checked))}
                    />
                    <span>{lang}</span>
                  </label>
                ))}
              </div>
              <Button onClick={onSave} disabled={saving} className="mt-4 rounded-full" data-testid="save-profile-btn">
                {saving ? "Saving..." : "Save profile"}
              </Button>
            </div>

            <div className="border-t border-border pt-5">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                <p className="text-xs uppercase tracking-[0.25em] font-black text-primary">City preferences</p>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                These options come from admin-managed cities and will update automatically.
              </p>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {cityOptions.map((city) => (
                  <label key={city} className="flex items-center gap-2 rounded-xl border border-border p-3 text-sm">
                    <Checkbox
                      checked={cityPrefs.includes(city)}
                      onCheckedChange={(checked) => toggleCity(city, Boolean(checked))}
                    />
                    <span>{city}</span>
                  </label>
                ))}
              </div>
            </div>

            <QuickActions user={user} navigate={navigate} />
            <ConnectPayoutsCard />
          </div>

          <aside className="space-y-6">
            <Panel title="My tickets" icon={Ticket} action="View all" actionTo="/bookings">
              {upcomingBookings.length === 0 ? (
                <EmptyLine text="No tickets yet." />
              ) : upcomingBookings.map((b) => (
                <button
                  key={b.booking_id}
                  type="button"
                  onClick={() => setActiveTicket(b)}
                  className="w-full text-left rounded-xl border border-border p-3 hover:bg-muted cursor-pointer"
                >
                  <p className="font-bold text-sm line-clamp-1">{b.item_title || b.content_title}</p>
                  <p className="text-xs text-muted-foreground">{b.quantity || 1} ticket(s) · {b.status || "confirmed"}</p>
                </button>
              ))}
            </Panel>

            <Panel title="Notifications" icon={Bell}>
              {notifications.slice(0, 4).length === 0 ? (
                <EmptyLine text="No notifications yet." />
              ) : notifications.slice(0, 4).map((n) => {
                const payloadRoute = n.data?.route;
                const href = typeof payloadRoute === "string" && payloadRoute.startsWith("/")
                  ? payloadRoute
                  : n.type === "admin_event_review"
                    ? `/admin-panel?tab=pending${n.event_id ? `&event=${n.event_id}` : ""}`
                    : ["event_rejected", "event_needs_changes"].includes(n.type)
                      ? "/my-events"
                      : n.event_id ? `/organiser/${n.event_id}` : "/profile";
                return (
                <a
                  key={n.notification_id}
                  href={href}
                  className="block rounded-xl border border-border p-3 hover:bg-muted"
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />}
                    <div>
                      <p className="font-bold text-sm">{n.title}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2">{n.body}</p>
                    </div>
                  </div>
                </a>
              );})}
            </Panel>
          </aside>
        </section>

        <section className="rounded-2xl border border-border p-5 md:p-6">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-[0.25em] font-black text-primary">My events</p>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" asChild><a href="/my-events">View all</a></Button>
              <Button size="sm" variant="ghost" asChild><a href="/create-event">+ New</a></Button>
            </div>
          </div>
          {myEvents.length === 0 ? (
            <p className="text-xs text-muted-foreground mt-3">You haven't submitted any events yet.</p>
          ) : (
            <div className="mt-4 grid md:grid-cols-2 gap-3">
              {myEvents.map((e) => {
                const eventId = getEventId(e);
                const poster = getEventPoster(e);
                return (
                  <a
                    key={eventId || e.title}
                    data-testid={`my-event-${eventId || "unknown"}`}
                    className="w-full p-3 rounded-xl border border-border flex items-center gap-3 hover:bg-muted text-left"
                    href={eventId ? `/organiser/${eventId}` : "/my-events"}
                  >
                    <img src={poster} alt="" loading="lazy" decoding="async" className="h-14 w-14 rounded-lg object-cover bg-muted" onError={(event) => applyPosterFallback(event, e)} />
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm line-clamp-1">{e.title}</p>
                      <p className="text-xs text-muted-foreground">{e.date} · {e.city}</p>
                    </div>
                    <Badge variant={e.status === "rejected" ? "destructive" : e.status === "needs_changes" ? "secondary" : "outline"}>{e.status}</Badge>
                  </a>
                );
              })}
            </div>
          )}
        </section>
        <QRTicketDialog open={!!activeTicket} onOpenChange={(open) => !open && setActiveTicket(null)} booking={activeTicket} />
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl bg-muted/60 p-3">
      <p className="text-xl font-black">{value}</p>
      <p className="text-[11px] text-muted-foreground">{label}</p>
    </div>
  );
}

function ReadOnlyField({ label, value, mono = false }) {
  return (
    <div>
      <label className="text-xs font-bold">{label}</label>
      <div className={`mt-1 rounded-md border border-input bg-muted/40 px-3 py-2 text-sm ${mono ? "font-mono text-xs" : ""}`}>
        {value || "-"}
      </div>
    </div>
  );
}

function ToggleRow({ checked, onCheckedChange, icon: Icon, title, body, className = "" }) {
  return (
    <label className={`flex items-start gap-3 rounded-xl border border-border p-3 text-sm ${className}`}>
      <Checkbox checked={checked} onCheckedChange={(value) => onCheckedChange(Boolean(value))} />
      <Icon className="mt-0.5 h-4 w-4 text-primary shrink-0" />
      <span>
        <span className="block font-bold">{title}</span>
        <span className="block text-xs text-muted-foreground mt-1">{body}</span>
      </span>
    </label>
  );
}

function ChipGroup({ title, options, selected, onToggle }) {
  if (!options.length) return null;
  return (
    <div>
      <p className="text-xs font-bold mb-2">{title}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {options.map((option) => (
          <label key={option} className="flex items-center gap-2 rounded-xl border border-border p-3 text-sm">
            <Checkbox
              checked={selected.includes(option)}
              onCheckedChange={(checked) => onToggle(option, Boolean(checked))}
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function QuickActions({ user }) {
  const isAdmin = user.role === "admin" || user.is_admin;
  const actions = [
    { label: "My bookings", icon: Receipt, to: "/bookings" },
    { label: "My events", icon: Calendar, to: "/my-events" },
    { label: "Create event", icon: Calendar, to: "/create-event" },
    { label: "Scan tickets", icon: ScanLine, to: "/scanner" },
    ...(isAdmin ? [{ label: "Admin panel", icon: ShieldCheck, to: "/admin-panel" }] : []),
    { label: "Saved events", icon: Heart, disabled: true },
    { label: "Payments", icon: CreditCard, disabled: true },
  ];
  return (
    <div className="border-t border-border pt-5">
      <p className="text-xs uppercase tracking-[0.25em] font-black text-primary">Quick actions</p>
      <div className="mt-4 grid sm:grid-cols-3 gap-2">
        {actions.map((action) => {
          const Icon = action.icon;
          if (action.disabled) {
            return (
              <Button key={action.label} variant="outline" disabled className="rounded-full justify-start">
                <Icon className="h-4 w-4 mr-2" /> {action.label}
              </Button>
            );
          }
          return (
            <Button
              key={action.label}
              variant="outline"
              asChild
              className="rounded-full justify-start cursor-pointer"
            >
              <a href={action.to}>
                <Icon className="h-4 w-4 mr-2" /> {action.label}
              </a>
            </Button>
          );
        })}
      </div>
    </div>
  );
}

function Panel({ title, icon: Icon, action, actionTo, children }) {
  return (
    <section className="rounded-2xl border border-border p-5 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary" />
          <p className="text-xs uppercase tracking-[0.2em] font-black text-primary">{title}</p>
        </div>
        {action && actionTo && <Button size="sm" variant="ghost" asChild><a href={actionTo}>{action}</a></Button>}
      </div>
      {children}
    </section>
  );
}

function EmptyLine({ text }) {
  return <p className="text-xs text-muted-foreground">{text}</p>;
}

function ConnectPayoutsCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/organizer/connect/status");
      setStatus(data);
    } catch { /* noop */ }
  };
  useEffect(() => { refresh(); }, []);

  const onConnect = async () => {
    setBusy(true);
    try {
      const { data } = status?.connected
        ? await api.post("/organizer/connect/dashboard-link")
        : await api.post("/organizer/connect/onboard", { origin_url: window.location.origin });
      if (data.sandbox) {
        toast.message(data.note || "Sandbox: Connect simulated.");
        await refresh();
      } else if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connect onboarding failed");
    } finally { setBusy(false); }
  };

  if (!status) return null;

  const paymentMode = status.payment_mode || "disabled";
  const expressEnabled = paymentMode === "stripe_connect";
  const statusMessage = paymentMode === "disabled"
    ? "Namaste ticket payments are disabled for this organiser. Contact admin to choose a collection route."
    : paymentMode === "platform"
      ? "Namaste will collect approved ticket sales and record your balance for manual settlement."
      : status.connected
        ? "Your Stripe Express account is active. Eligible ticket sales route automatically after the Namaste fee."
        : status.account_id
          ? "Stripe Express is enabled by admin. Complete onboarding before ticket checkout can open."
          : "Stripe Express is enabled by admin. Connect your bank and complete Stripe onboarding to activate checkout.";

  return (
    <div className="border-t border-border pt-5" data-testid="connect-card">
      <div className="flex items-start gap-4">
        <Megaphone className="h-5 w-5 text-primary mt-1" />
        <div className="flex-1">
          <p className="text-xs uppercase tracking-[0.25em] font-black text-primary">
            Organizer payouts
          </p>
          <p className="text-sm mt-1">
            {status.connected && expressEnabled
              ? <span className="text-primary font-bold">{statusMessage}</span>
              : statusMessage}
          </p>
          {status.account_id && (
            <p className="text-[11px] font-mono text-muted-foreground mt-1">
              {status.account_id}
            </p>
          )}
        </div>
        {expressEnabled && (
          <Button
            size="sm"
            onClick={onConnect}
            disabled={busy}
            data-testid="connect-btn"
            className="rounded-full"
          >
            {busy ? "Opening..." : status.connected ? "Manage" : status.account_id ? "Continue" : "Connect"}
          </Button>
        )}
      </div>
    </div>
  );
}

function OrganizerFinancePanel() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/organizer/finance-summary")
      .then(({ data }) => setSummary(data))
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <section className="border-y border-border py-6 text-sm text-muted-foreground">Loading organiser revenue...</section>;
  if (!summary) return null;
  const totals = summary.totals || {};
  const pounds = (pence) => `£${(Number(pence || 0) / 100).toFixed(2)}`;
  return (
    <section className="border-y border-border py-6 space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] font-black text-primary">Organiser revenue</p>
          <h2 className="mt-1 font-display text-2xl font-black">Sales and settlement</h2>
        </div>
        <p className="text-xs text-muted-foreground">
          Future buyer booking fee: {Number(summary.organizer?.platform_fee_pct || 0).toFixed(1)}% + {pounds(summary.organizer?.platform_fee_fixed_pence)} per order
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
        <Stat label="Events" value={totals.events || 0} />
        <Stat label="Tickets sold" value={totals.tickets || 0} />
        <Stat label="Checked in" value={totals.checkins || 0} />
        <Stat label="Gross sales" value={pounds(totals.gross_pence)} />
        <Stat label="Net sales" value={pounds(totals.net_sales_pence)} />
        <Stat label="Refunds" value={pounds(totals.refund_pence)} />
        <Stat label="Namaste fees" value={pounds(totals.platform_fee_pence)} />
        <Stat label="Your share" value={pounds(totals.organizer_due_pence)} />
        <Stat label="Manual due" value={pounds(totals.manual_due_pence)} />
      </div>
      <div className="divide-y divide-border border-y border-border">
        {(summary.events || []).map((event) => (
          <a key={event.event_id} href={`/organiser/${event.event_id}?tab=reports`} className="grid gap-2 py-3 text-sm hover:bg-muted/40 md:grid-cols-[1fr_repeat(4,110px)] md:px-2">
            <div>
              <p className="font-bold">{event.title}</p>
              <p className="text-xs text-muted-foreground">{event.date || "No date"} · {event.tickets || 0} sold · {event.checkins || 0} checked in</p>
            </div>
            <span><span className="text-xs text-muted-foreground">Gross</span><br />{pounds(event.gross_pence)}</span>
            <span><span className="text-xs text-muted-foreground">Refunds</span><br />{pounds(event.refund_pence)}</span>
            <span><span className="text-xs text-muted-foreground">Fee</span><br />{pounds(event.platform_fee_pence)}</span>
            <span><span className="text-xs text-muted-foreground">Your share</span><br />{pounds(event.organizer_due_pence)}</span>
          </a>
        ))}
        {!summary.events?.length && <p className="py-4 text-sm text-muted-foreground">No organiser events yet.</p>}
      </div>
    </section>
  );
}
